import {
  type AllocationDraftCell,
  buildCustomAllocationSubAccounts,
  type SubAccountCustomAllocationInput,
  storedPortfolioFraction,
} from '@ovComponents/4-module/widgets/portfolio/components/buildCustomAllocationInput';
import { tradeableAllocationSubAccounts } from '@ovComponents/4-module/widgets/portfolio/components/tradeableAllocationMembers';
import type {
  AccountAllocationGridAccount,
  AccountAllocationGridHolding,
  AccountAllocationGridReport,
} from './types';

const CASH_FINANCIAL_PRODUCT_ID = 'CASH';

type MatrixRow = AccountAllocationGridHolding & {
  financialProduct: AccountAllocationGridHolding['financialProduct'] & {
    id: string;
  };
};

const isCashHolding = (holding: AccountAllocationGridHolding) =>
  holding.financialProduct.id === CASH_FINANCIAL_PRODUCT_ID ||
  holding.financialProduct.isCash === true;

export const matrixRows = (report: AccountAllocationGridReport): MatrixRow[] =>
  report.holdings.filter(
    (holding): holding is MatrixRow =>
      Boolean(holding.financialProduct.id) && !isCashHolding(holding),
  );

type CellFractionRow = Pick<
  AccountAllocationGridHolding,
  'expectedPercentage'
> & {
  financialProduct: { id: string };
};

export const cellFraction = (
  account: AccountAllocationGridAccount,
  row: CellFractionRow,
  isAssetClass = false,
): number | undefined => {
  const held = account.holdings.find(
    (holding) => holding.financialProduct.id === row.financialProduct.id,
  );
  if (!held) return undefined;
  return storedPortfolioFraction(held, isAssetClass) ?? row.expectedPercentage;
};

const isHeldCell = (account: AccountAllocationGridAccount, row: MatrixRow) =>
  account.holdings.some(
    (holding) => holding.financialProduct.id === row.financialProduct.id,
  );

// One cell per held, non-cash fund; an undefined fraction leaves the cell out.
const matrixPayload = (
  report: AccountAllocationGridReport,
  accounts: AccountAllocationGridAccount[],
  fractionOf: (
    account: AccountAllocationGridAccount,
    row: MatrixRow,
  ) => number | undefined,
): SubAccountCustomAllocationInput[] => {
  const rows = matrixRows(report);
  const cells: AllocationDraftCell[] = [];

  for (const account of accounts) {
    for (const row of rows) {
      const fraction = fractionOf(account, row);
      if (fraction === undefined) continue;
      cells.push({
        subAccountId: account.subAccount.id,
        financialProductId: row.financialProduct.id,
        customExpectedPercentage: fraction,
      });
    }
  }

  return buildCustomAllocationSubAccounts(cells);
};

export const placeholderPayload = (
  report: AccountAllocationGridReport,
): SubAccountCustomAllocationInput[] =>
  matrixPayload(report, report.subAccounts, cellFraction);

// The full edited matrix for staging, in portfolio-level fractions (what
// displayFraction returns). Covers every cell the grid lets an adviser lock:
// held, non-cash funds of tradeable accounts.
export const buildDraftMatrix = (
  report: AccountAllocationGridReport,
  displayFraction: (
    subAccountId: string,
    financialProductId: string,
    modelTarget: number,
  ) => number,
): SubAccountCustomAllocationInput[] =>
  matrixPayload(
    report,
    tradeableAllocationSubAccounts(report.subAccounts),
    (account, row) =>
      !isHeldCell(account, row)
        ? undefined
        : displayFraction(
            account.subAccount.id,
            row.financialProduct.id,
            row.expectedPercentage,
          ),
  );
